import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import useField, { UseFieldType, useFieldInitialState } from './useField';
import { IContact } from '@trii/types/dist/Contacts';
import { UseDateState, UseLinkContact, SelectedContact } from './useEvent';

export interface UseTask {
  titleField: UseFieldType;
  descriptionField: UseFieldType;
  date: UseDateState;
  linkContact: UseLinkContact;
  create: () => {
    title: string;
    description: string;
    startAt: string;
    endAt: string;
    contactId: string | null;
  };
  resetFields: () => void;
}

// Initial state
const useDateStateInitial: UseDateState = {
  state: {
    selectedStartDate: dayjs(),
    selectedEndDate: dayjs(),
    error: false,
  },
  action: {
    setSelectedStartDate: () => {},
    setSelectedEndDate: () => {},
    setError: () => {},
    handleSelect: () => () => {},
  },
};

const useLinkContactInitial: UseLinkContact = {
  state: {
    search: '',
    selectedContact: null,
  },
  action: {
    handleSelectContact: () => {},
    handleRemoveContact: () => {},
    handleSearchChange: () => {},
    resetField: () => {},
  },
};

export const useTaskInitialState: UseTask = {
  titleField: useFieldInitialState,
  descriptionField: useFieldInitialState,
  date: useDateStateInitial,
  linkContact: useLinkContactInitial,
  create: () => ({
    title: '',
    description: '',
    startAt: '',
    endAt: '',
    contactId: null,
  }),
  resetFields: () => {},
};

// Custom Hook para gestionar los estados del Date
const useDateState = (initialStart?: Dayjs, initialEnd?: Dayjs): UseDateState => {
  const [selectedStartDate, setSelectedStartDate] = useState<Dayjs>(initialStart ?? dayjs());
  const [selectedEndDate, setSelectedEndDate] = useState<Dayjs>(initialEnd ?? dayjs());
  const [error, setError] = useState(false);

  const handleSelect = useCallback(
    (setter: (date: Dayjs) => void) => (date: Dayjs) => {
      setter(date);
    },
    [],
  );

  const state = useMemo(
    () => ({
      selectedStartDate,
      selectedEndDate,
      error,
    }),
    [selectedStartDate, selectedEndDate, error],
  );

  const action = useMemo(
    () => ({
      setSelectedStartDate,
      setSelectedEndDate,
      setError,
      handleSelect,
    }),
    [handleSelect],
  );

  return { state, action };
};

// Custom Hook para gestionar contacto relacionado (single-select)
const useLinkContactState = (initialContact?: { id: string; name: string } | null): UseLinkContact => {
  const [search, setSearch] = useState('');
  const [selectedContact, setSelectedContact] = useState<SelectedContact | null>(
    initialContact?.id ? { id: initialContact.id, name: initialContact.name } : null
  );

  // Sincronizar cuando cambia initialContact (usando key para detectar cambios en objetos)
  const contactKey = initialContact ? `${initialContact.id}|${initialContact.name}` : '';
  useEffect(() => {
    setSelectedContact(
      initialContact?.id ? { id: initialContact.id, name: initialContact.name } : null
    );
  }, [contactKey, initialContact]);

  const handleSelectContact = useCallback((contact: IContact) => {
    setSelectedContact({ id: contact.id, name: contact.name ?? '' });
    setSearch('');
  }, []);

  const handleRemoveContact = useCallback(() => {
    setSelectedContact(null);
  }, []);

  const handleSearchChange = useCallback((newSearch: string) => {
    setSearch(newSearch);
  }, []);

  // Resetear al valor inicial (como useField) para que al cerrar/abrir el modal se restaure correctamente
  const resetField = useCallback(() => {
    setSearch('');
    setSelectedContact(
      initialContact?.id ? { id: initialContact.id, name: initialContact.name } : null
    );
  }, [initialContact]);

  const state = useMemo(
    () => ({
      search,
      selectedContact,
    }),
    [search, selectedContact],
  );

  const action = useMemo(
    () => ({
      handleSelectContact,
      handleRemoveContact,
      handleSearchChange,
      resetField,
    }),
    [handleSelectContact, handleRemoveContact, handleSearchChange, resetField],
  );

  return {
    state,
    action,
  };
};

// Hook principal
const useTask = (selectedTask?: any): UseTask => {
  // 🔒 Ref para trackear la última tarea cargada y evitar loops
  const lastTaskIdRef = useRef<string | null>(null);

  // Campos de texto
  const titleField = useField('text', selectedTask?.title ?? '');
  const descriptionField = useField('text', selectedTask?.description ?? '');

  // Estado de fechas (para tareas, start y end son la misma fecha)
  const date = useDateState(
    selectedTask?.startAt ? dayjs(selectedTask.startAt) : undefined,
    selectedTask?.startAt ? dayjs(selectedTask.startAt) : undefined,
  );

  // Contacto relacionado
  const linkContact = useLinkContactState(
    selectedTask?.relatedContact ?? selectedTask?.raw?.relatedContact
  );

  // Crear payload para enviar al backend
  const create = useCallback(() => {
    return {
      title: titleField.attributes.value,
      description: descriptionField.attributes.value,
      startAt: date.state.selectedStartDate.toISOString(),
      endAt: date.state.selectedStartDate.toISOString(), // tareas usan la misma fecha
      contactId: linkContact.state.selectedContact?.id || null,
    };
  }, [
    titleField.attributes.value,
    descriptionField.attributes.value,
    date.state.selectedStartDate,
    linkContact.state.selectedContact,
  ]);

  // Resetear todos los campos
  const resetFields = useCallback(() => {
    titleField.actions.resetValue();
    descriptionField.actions.resetValue();
    linkContact.action.resetField();
    date.action.setError(false);
    // Resetear ref para permitir re-sync al abrir la misma tarea nuevamente
    lastTaskIdRef.current = null;
  }, [titleField.actions, descriptionField.actions, linkContact.action, date.action]);

  // 🔄 SINCRONIZACIÓN: Actualizar estados cuando selectedTask cambia
  // Esto previene problemas al cambiar de modo view → edit → duplicate
  useEffect(() => {
    const currentTaskId = selectedTask?.id;

    // 🔒 PROTECCIÓN CONTRA LOOPS: Solo sincronizar si el ID de la tarea cambió
    if (!selectedTask || lastTaskIdRef.current === currentTaskId) {
      return;
    }

    lastTaskIdRef.current = currentTaskId;

    // Log para debugging
    console.log('[useTask] Sincronizando tarea:', {
      id: selectedTask.id,
      title: selectedTask.title,
      description: selectedTask.description,
      startAt: selectedTask.startAt,
      relatedContact: selectedTask.relatedContact,
    });

    // Sincronizar campos de texto
    titleField.actions.changeValue(selectedTask.title ?? '');
    descriptionField.actions.changeValue(selectedTask.description ?? '');

    // Sincronizar fecha (para tareas, start y end son la misma fecha)
    if (selectedTask.startAt) {
      const taskDate = dayjs(selectedTask.startAt);
      date.action.setSelectedStartDate(taskDate);
      date.action.setSelectedEndDate(taskDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTask?.id]);

  // 🔄 SINCRONIZACIÓN ADICIONAL: Actualizar relatedContact cuando cambia (incluso si el ID de la tarea es el mismo)
  useEffect(() => {
    if (!selectedTask) return;

    const rc = selectedTask.relatedContact ?? selectedTask.raw?.relatedContact;
    
    // Comparar si el contacto actual es diferente al seleccionado
    const currentContactId = linkContact.state.selectedContact?.id;
    const newContactId = rc?.id;
    
    if (currentContactId !== newContactId) {
      if (newContactId) {
        linkContact.action.handleSelectContact({
          id: newContactId,
          name: rc.name ?? '',
        } as any);
      } else {
        linkContact.action.resetField();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTask?.relatedContact, selectedTask?.raw?.relatedContact]);

  return useMemo(
    () => ({
      titleField,
      descriptionField,
      date,
      linkContact,
      create,
      resetFields,
    }),
    [titleField, descriptionField, date, linkContact, create, resetFields],
  );
};

export default useTask;
